import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { countdownParts, initialsFor } from '@fanvote25/shared';
import './ui.css';

/* ------------------------------------------------------------------ */
/* Card                                                                */
/* ------------------------------------------------------------------ */

export function Card({
  title,
  subtitle,
  action,
  children,
  className,
  padded = true,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section className={`fv-card ${className ?? ''}`}>
      {(title || action) && (
        <header className="fv-card-head">
          <h2 className="fv-card-title">
            {title}
            {subtitle && <small>{subtitle}</small>}
          </h2>
          {action}
        </header>
      )}
      <div className={padded ? 'fv-card-body' : undefined}>{children}</div>
    </section>
  );
}

export function CardLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link className="fv-card-link" to={to}>
      {children} <span aria-hidden="true">&rarr;</span>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Rank + movement                                                     */
/* ------------------------------------------------------------------ */

export function RankBadge({ rank, size = 40 }: { rank: number; size?: number }) {
  return (
    <span
      className="fv-rank fv-rank-badge"
      data-top={rank <= 3 ? rank : undefined}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.52) }}
    >
      {rank}
    </span>
  );
}

export function Movement({
  movement,
  isNew,
  droppedOut,
  showZero = true,
}: {
  movement?: number | null;
  isNew?: boolean;
  droppedOut?: boolean;
  showZero?: boolean;
}) {
  if (droppedOut) {
    return <span className="fv-move fv-move-down">OUT</span>;
  }
  if (isNew) {
    return <span className="fv-move fv-move-new">NEW</span>;
  }
  if (movement === null || movement === undefined || movement === 0) {
    if (!showZero) return null;
    return (
      <span className="fv-move fv-move-flat" aria-label="no change">
        &mdash;
      </span>
    );
  }
  const up = movement > 0;
  return (
    <span
      className={`fv-move ${up ? 'fv-move-up' : 'fv-move-down'}`}
      aria-label={`${up ? 'up' : 'down'} ${Math.abs(movement)}`}
    >
      <span aria-hidden="true">{up ? '▲' : '▼'}</span>
      {Math.abs(movement)}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Progress                                                            */
/* ------------------------------------------------------------------ */

export function ProgressBar({
  value,
  max,
  label,
}: {
  value: number;
  max: number;
  label?: string;
}) {
  const pct = max === 0 ? 0 : Math.min(100, Math.round((value / max) * 100));
  return (
    <div
      className="fv-progress"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label ?? `${value} of ${max}`}
    >
      <div
        className="fv-progress-fill"
        data-complete={value >= max}
        data-low={pct < 35}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Countdown                                                           */
/* ------------------------------------------------------------------ */

function useTick(intervalMs = 1000) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((value) => value + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
}

/** Big DAYS / HOURS / MINUTES / SECONDS blocks for the hero. */
export function CountdownBlocks({ target, compact = false }: { target: string; compact?: boolean }) {
  useTick();
  const parts = countdownParts(Date.parse(target) - Date.now());
  const blocks = [
    { value: parts.days, label: 'Days' },
    { value: parts.hours, label: 'Hours' },
    { value: parts.minutes, label: 'Minutes' },
    { value: parts.seconds, label: 'Seconds' },
  ];
  return (
    <div className={`fv-countdown ${compact ? 'fv-countdown-compact' : ''}`}>
      {blocks.map((block) => (
        <div className="fv-countdown-block" key={block.label}>
          <div className="fv-countdown-value fv-nums">{String(block.value).padStart(2, '0')}</div>
          <div className="fv-countdown-label">{block.label}</div>
        </div>
      ))}
    </div>
  );
}

/** Inline "02:14:37" clock for urgent banners. */
export function CountdownClock({ target }: { target: string }) {
  useTick();
  const remaining = Date.parse(target) - Date.now();
  const parts = countdownParts(remaining);
  const hours = parts.days * 24 + parts.hours;
  return (
    <span className="fv-nums">
      {[hours, parts.minutes, parts.seconds].map((part) => String(part).padStart(2, '0')).join(':')}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Avatar                                                              */
/* ------------------------------------------------------------------ */

export function Avatar({
  name,
  color,
  size = 32,
}: {
  name: string;
  color: string;
  size?: number;
}) {
  return (
    <span
      className="fv-avatar"
      style={{ width: size, height: size, background: color, fontSize: Math.round(size * 0.38) }}
      aria-hidden="true"
    >
      {initialsFor(name)}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Loading, empty and error states                                     */
/* ------------------------------------------------------------------ */

export function Skeleton({
  height = 16,
  width = '100%',
  radius = 6,
  className,
}: {
  height?: number | string;
  width?: number | string;
  radius?: number;
  className?: string;
}) {
  return (
    <div
      className={`fv-skeleton ${className ?? ''}`}
      style={{ height, width, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

export function LoadingPanel({ message, rows = 4 }: { message: string; rows?: number }) {
  return (
    <div className="fv-loading-panel" role="status" aria-live="polite">
      <p className="fv-loading-note">{message}</p>
      <div className="fv-stack" style={{ gap: 8, marginTop: 14 }}>
        {Array.from({ length: rows }, (_, index) => (
          <Skeleton key={index} height={index === 0 ? 48 : 40} />
        ))}
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  message,
  action,
  icon,
}: {
  title: string;
  message?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="fv-empty">
      {icon && <div className="fv-empty-icon">{icon}</div>}
      <h3>{title}</h3>
      {message && <p>{message}</p>}
      {action}
    </div>
  );
}

export function ErrorState({
  title = 'We could not load that.',
  message,
  onRetry,
}: {
  title?: string;
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="fv-empty">
      <h3>{title}</h3>
      <p>{message ?? 'The last successful data is still being shown where we have it.'}</p>
      {onRetry && (
        <button type="button" className="fv-btn fv-btn-primary" onClick={onRetry}>
          Try Again
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Modal / sheet                                                       */
/* ------------------------------------------------------------------ */

export function Modal({
  open,
  onClose,
  title,
  children,
  wide = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previouslyFocused = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fv-modal-backdrop" onClick={onClose} role="presentation">
      <div
        ref={ref}
        className={`fv-modal ${wide ? 'fv-modal-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="fv-modal-head">
          <h2 className="fv-card-title">{title}</h2>
          <button type="button" className="fv-btn fv-btn-ghost fv-btn-sm" onClick={onClose} aria-label="Close">
            &#10005;
          </button>
        </header>
        <div className="fv-modal-body">{children}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tabs                                                                */
/* ------------------------------------------------------------------ */

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: T; label: string; count?: number }[];
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="fv-tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          className={`fv-tab ${active === tab.id ? 'fv-tab-active' : ''}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
          {tab.count !== undefined && <span className="fv-tab-count">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Stat tile                                                           */
/* ------------------------------------------------------------------ */

export function StatTile({
  icon,
  value,
  label,
  note,
  tone,
}: {
  icon?: ReactNode;
  value: ReactNode;
  label: string;
  note?: ReactNode;
  tone?: 'up' | 'down';
}) {
  return (
    <div className="fv-stat-tile">
      {icon && <div className="fv-stat-icon">{icon}</div>}
      <div className="fv-stat-main">
        <div className="fv-stat-value fv-nums">{value}</div>
        <div className="fv-stat-label">{label}</div>
        {note && (
          <div className={`fv-stat-note ${tone ? `fv-move-${tone}` : ''}`}>{note}</div>
        )}
      </div>
    </div>
  );
}
